import { MedicalPaOrder } from "../../../data-model/medicalPaOrdersModel";
import { ScreenNamesForBigQuery } from "../../../enums/eventsData";
import { EventsName } from "../../../enums/eventsName";
import { apiWorkerManager } from "../../../workerManagers/workerManager";

interface BaseLogEventParams {
  singleMedicalPaOrder: MedicalPaOrder | null;
  orgId: string;
  userId: string;
  userEmail: string;
  additionalData?: Record<string, any>;
}

interface LogEventParams extends BaseLogEventParams {
  eventName: EventsName;
}

/**
 * Logs events to BigQuery with common parameters for final documents screen
 * @param params - Event logging parameters
 */
export const logFinalDocumentEvent = async ({
  eventName,
  singleMedicalPaOrder,
  orgId,
  userId,
  userEmail,
  additionalData = {},
}: LogEventParams) => {
  try {
    await apiWorkerManager.enqueueAndProcess({
      patient_id: singleMedicalPaOrder?.patient_id ?? "",
      order_id: singleMedicalPaOrder?.order_id ?? "",
      org_id: orgId ?? "",
      event_name: eventName,
      user_id: userId ?? "",
      user_email: userEmail ?? "",
      additional_data: {
        screen_name: ScreenNamesForBigQuery.FINAL_DOCUMENT_SCREEN,
        payer_name: singleMedicalPaOrder?.coverage?.primary?.payer_name ?? "",
        regimen_name: singleMedicalPaOrder?.payload?.regimen_name ?? "",
        ...additionalData,
      },
    });
  } catch (error) {
    console.error("Failed to log event to BigQuery:", error);
  }
};

/**
 * Logs submit form click event
 */
export const logSubmitFormClick = async (params: BaseLogEventParams) => {
  await logFinalDocumentEvent({
    ...params,
    eventName: EventsName.SUBMIT_FORM_CLICKED,
    additionalData: {
      source: ScreenNamesForBigQuery.FINAL_DOCUMENT_SCREEN,
      ...params.additionalData,
    },
  });
};

/**
 * Logs uploading attachment completed event
 */
export const logUploadingAttachmentCompleted = async (
  params: BaseLogEventParams,
) => {
  await logFinalDocumentEvent({
    ...params,
    eventName: EventsName.UPLOADING_ATTACHMENT_COMPLETED,
  });
};

/**
 * Logs submission initiated event
 */
export const logSubmissionInitiated = async (params: BaseLogEventParams) => {
  await logFinalDocumentEvent({
    ...params,
    eventName: EventsName.SUBMISSION_INITIATED,
    additionalData: {
      source: ScreenNamesForBigQuery.FINAL_DOCUMENT_SCREEN,
      ...params.additionalData,
    },
  });
};

/**
 * Logs manage attachments clicked event
 */
export const logManageAttachmentsClick = async (params: BaseLogEventParams) => {
  await logFinalDocumentEvent({
    ...params,
    eventName: EventsName.MANAGE_ATTACHMENTS_CLICKED,
    additionalData: {
      source: ScreenNamesForBigQuery.FINAL_DOCUMENT_SCREEN,
      ...params.additionalData,
    },
  });
};
